/**
 * UBOCollectionStep Component
 * ===========================
 * Collects Ultimate Beneficial Owners (UBOs) for KYB verification.
 * Features dynamic form to add multiple beneficial owners,
 * ownership percentage tracking, and certification.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Plus,
  Trash2,
  User,
  Percent,
  Mail,
  Calendar,
  Globe,
  CheckCircle2,
  Loader2,
  ShieldCheck,
} from 'lucide-react';

const NATIONALITIES = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'AU', label: 'Australia' },
  { value: 'SG', label: 'Singapore' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'JP', label: 'Japan' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'IE', label: 'Ireland' },
  { value: 'IN', label: 'India' },
  { value: 'CN', label: 'China' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'OTHER', label: 'Other' },
];

const createEmptyUBO = () => ({
  id: Date.now().toString(),
  full_name: '',
  date_of_birth: '',
  nationality: '',
  ownership_percentage: '',
  is_control_person: false,
  is_submitter: false,
  email: '',
});

const UBOCard = ({ ubo, index, onChange, onRemove, canRemove, errors = {} }) => {
  const [expanded, setExpanded] = useState(true);

  const handleChange = (field, value) => {
    onChange(ubo.id, { ...ubo, [field]: value });
  };

  const getFieldError = (field) => errors[`${ubo.id}_${field}`];

  return (
    <div className={`ubo-card ${expanded ? 'expanded' : ''}`}>
      <div className="ubo-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="ubo-avatar">
          <User size={18} />
        </div>
        <div className="ubo-header-content">
          <span className="ubo-title">
            {ubo.full_name || `Beneficial Owner ${index + 1}`}
          </span>
          {ubo.ownership_percentage && (
            <span className="ubo-ownership">{ubo.ownership_percentage}% ownership</span>
          )}
        </div>
        {canRemove && (
          <button
            type="button"
            className="remove-ubo-btn"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(ubo.id);
            }}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {expanded && (
        <div className="ubo-card-body">
          <div className="form-group">
            <label className="form-label">
              <User size={14} />
              Full Legal Name <span className="required">*</span>
            </label>
            <input
              type="text"
              className={`form-input ${getFieldError('full_name') ? 'error' : ''}`}
              value={ubo.full_name}
              onChange={(e) => handleChange('full_name', e.target.value)}
              placeholder="John Smith"
            />
            {getFieldError('full_name') && (
              <span className="error-message">
                <AlertCircle size={12} />
                {getFieldError('full_name')}
              </span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <Calendar size={14} />
                Date of Birth <span className="required">*</span>
              </label>
              <input
                type="date"
                className={`form-input ${getFieldError('date_of_birth') ? 'error' : ''}`}
                value={ubo.date_of_birth}
                onChange={(e) => handleChange('date_of_birth', e.target.value)}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
              />
              {getFieldError('date_of_birth') && (
                <span className="error-message">
                  <AlertCircle size={12} />
                  {getFieldError('date_of_birth')}
                </span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <Globe size={14} />
                Nationality <span className="required">*</span>
              </label>
              <select
                className={`form-select ${getFieldError('nationality') ? 'error' : ''}`}
                value={ubo.nationality}
                onChange={(e) => handleChange('nationality', e.target.value)}
              >
                <option value="">Select nationality...</option>
                {NATIONALITIES.map(nat => (
                  <option key={nat.value} value={nat.value}>{nat.label}</option>
                ))}
              </select>
              {getFieldError('nationality') && (
                <span className="error-message">
                  <AlertCircle size={12} />
                  {getFieldError('nationality')}
                </span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <Percent size={14} />
                Ownership % <span className="required">*</span>
              </label>
              <input
                type="number"
                className={`form-input ${getFieldError('ownership_percentage') ? 'error' : ''}`}
                value={ubo.ownership_percentage}
                onChange={(e) => handleChange('ownership_percentage', e.target.value)}
                placeholder="25"
                min="0"
                max="100"
                step="0.01"
              />
              {getFieldError('ownership_percentage') && (
                <span className="error-message">
                  <AlertCircle size={12} />
                  {getFieldError('ownership_percentage')}
                </span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <Mail size={14} />
                Email <span className="optional">(optional)</span>
              </label>
              <input
                type="email"
                className={`form-input ${getFieldError('email') ? 'error' : ''}`}
                value={ubo.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="john@example.com"
              />
              {getFieldError('email') && (
                <span className="error-message">
                  <AlertCircle size={12} />
                  {getFieldError('email')}
                </span>
              )}
            </div>
          </div>

          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={ubo.is_control_person}
                onChange={(e) => handleChange('is_control_person', e.target.checked)}
              />
              <div className="checkbox-custom">
                {ubo.is_control_person && <CheckCircle2 size={12} />}
              </div>
              <span>This person has significant control over the company</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={ubo.is_submitter}
                onChange={(e) => handleChange('is_submitter', e.target.checked)}
              />
              <div className="checkbox-custom">
                {ubo.is_submitter && <CheckCircle2 size={12} />}
              </div>
              <span>This is the person submitting this verification</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

const OwnershipBar = ({ percentage }) => {
  const getBarColor = () => {
    if (percentage > 100) return '#EF4444';
    if (percentage >= 75) return '#10B981';
    if (percentage >= 50) return '#F59E0B';
    return '#6B7280';
  };

  const getStatusText = () => {
    if (percentage > 100) return `Over 100% (${percentage}%)`;
    if (percentage === 100) return 'Complete (100%)';
    return `${percentage}% of ownership documented`;
  };

  return (
    <div className="ownership-bar-container">
      <div className="ownership-bar-header">
        <span className="ownership-label">Total Ownership</span>
        <span className="ownership-value" style={{ color: getBarColor() }}>
          {getStatusText()}
        </span>
      </div>
      <div className="ownership-bar-track">
        <div
          className="ownership-bar-fill"
          style={{
            width: `${Math.min(percentage, 100)}%`,
            backgroundColor: getBarColor(),
          }}
        />
      </div>
    </div>
  );
};

export default function UBOCollectionStep({
  config = {},
  branding = {},
  sessionData = {},
  onComplete,
  onBack,
  onError,
  apiClient,
}) {
  const [animateIn, setAnimateIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [certified, setCertified] = useState(false);

  // Initialize UBOs from session data or create one empty UBO
  const [ubos, setUbos] = useState(() => {
    if (sessionData?.beneficial_owners?.length > 0) {
      return sessionData.beneficial_owners.map(ubo => ({
        ...ubo,
        id: ubo.id || Date.now().toString() + Math.random(),
      }));
    }
    return [createEmptyUBO()];
  });

  useEffect(() => {
    const timer = setTimeout(() => setAnimateIn(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Calculate total ownership
  const totalOwnership = ubos.reduce((sum, ubo) => {
    const pct = parseFloat(ubo.ownership_percentage) || 0;
    return sum + pct;
  }, 0);

  const handleUBOChange = useCallback((id, updatedUBO) => {
    setUbos(prev => prev.map(ubo => ubo.id === id ? updatedUBO : ubo));
    // Clear errors for this UBO when user makes changes
    setErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith(id)) delete newErrors[key];
      });
      return newErrors;
    });
  }, []);

  const handleAddUBO = useCallback(() => {
    setUbos(prev => [...prev, createEmptyUBO()]);
  }, []);

  const handleRemoveUBO = useCallback((id) => {
    setUbos(prev => prev.filter(ubo => ubo.id !== id));
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Validate each UBO
    ubos.forEach(ubo => {
      if (!ubo.full_name.trim()) {
        newErrors[`${ubo.id}_full_name`] = 'Name is required';
      }
      if (!ubo.date_of_birth) {
        newErrors[`${ubo.id}_date_of_birth`] = 'Date of birth is required';
      }
      if (!ubo.nationality) {
        newErrors[`${ubo.id}_nationality`] = 'Nationality is required';
      }
      if (!ubo.ownership_percentage) {
        newErrors[`${ubo.id}_ownership_percentage`] = 'Ownership % is required';
      } else {
        const pct = parseFloat(ubo.ownership_percentage);
        if (isNaN(pct) || pct < 0 || pct > 100) {
          newErrors[`${ubo.id}_ownership_percentage`] = 'Must be between 0-100';
        }
      }
      if (ubo.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ubo.email)) {
        newErrors[`${ubo.id}_email`] = 'Invalid email address';
      }
    });

    // Validate total ownership
    if (totalOwnership > 100) {
      newErrors.total_ownership = 'Total ownership cannot exceed 100%';
    }

    // Certification required
    if (!certified) {
      newErrors.certification = 'You must certify the information is accurate';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [ubos, totalOwnership, certified]);

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      // Format data for API
      const uboData = ubos.map(ubo => ({
        full_name: ubo.full_name.trim(),
        date_of_birth: ubo.date_of_birth,
        nationality: ubo.nationality,
        ownership_percentage: parseFloat(ubo.ownership_percentage),
        is_control_person: ubo.is_control_person,
        is_submitter: ubo.is_submitter,
        email: ubo.email.trim() || null,
      }));

      // Submit to API if client available
      if (apiClient?.completeStep) {
        await apiClient.completeStep('beneficial_owners', {
          beneficial_owners: uboData,
          certification_timestamp: new Date().toISOString(),
        });
      }

      onComplete({ beneficial_owners: uboData });
    } catch (err) {
      console.error('Failed to submit UBOs:', err);
      if (onError) {
        onError(err);
      }
      setErrors({ submit: err.message || 'Failed to save beneficial owner information' });
    } finally {
      setSubmitting(false);
    }
  };

  // Config-driven thresholds
  const minOwnershipThreshold = config?.min_ownership_threshold || 25;

  return (
    <div className={`sdk-step ubo-collection-step ${animateIn ? 'animate-in' : ''}`}>
      <div className="step-header">
        {onBack && (
          <button className="back-button" onClick={onBack} type="button">
            <ChevronLeft size={20} />
          </button>
        )}
        <div className="step-icon">
          <Users size={24} />
        </div>
      </div>

      <h2 className="step-title">Beneficial Owners</h2>
      <p className="step-description">
        List all individuals who own {minOwnershipThreshold}% or more of the company,
        or who have significant control over it.
      </p>

      <OwnershipBar percentage={Math.round(totalOwnership * 100) / 100} />

      <div className="ubo-list">
        {ubos.map((ubo, index) => (
          <UBOCard
            key={ubo.id}
            ubo={ubo}
            index={index}
            onChange={handleUBOChange}
            onRemove={handleRemoveUBO}
            canRemove={ubos.length > 1}
            errors={errors}
          />
        ))}
      </div>

      <button
        type="button"
        className="add-ubo-button"
        onClick={handleAddUBO}
      >
        <Plus size={18} />
        Add Another Owner
      </button>

      {errors.total_ownership && (
        <div className="total-error">
          <AlertCircle size={16} />
          {errors.total_ownership}
        </div>
      )}

      <div className="certification-section">
        <label className={`certification-checkbox ${errors.certification ? 'error' : ''}`}>
          <input
            type="checkbox"
            checked={certified}
            onChange={(e) => {
              setCertified(e.target.checked);
              if (errors.certification) {
                setErrors(prev => ({ ...prev, certification: null }));
              }
            }}
          />
          <div className="checkbox-custom">
            {certified && <ShieldCheck size={14} />}
          </div>
          <div className="certification-text">
            <strong>I certify</strong> that I am authorized to provide this information
            and that all details provided are accurate and complete to the best of my knowledge.
          </div>
        </label>
        {errors.certification && (
          <span className="error-message certification-error">
            <AlertCircle size={12} />
            {errors.certification}
          </span>
        )}
      </div>

      {errors.submit && (
        <div className="submit-error">
          <AlertCircle size={16} />
          {errors.submit}
        </div>
      )}

      <div className="step-actions">
        <button
          type="button"
          className="primary-button"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Loader2 size={18} className="spin" />
              Saving...
            </>
          ) : (
            <>
              Continue
              <ChevronRight size={18} />
            </>
          )}
        </button>
      </div>

      <style>{uboCollectionStyles}</style>
    </div>
  );
}

const uboCollectionStyles = `
  .ubo-collection-step {
    padding: 24px;
    max-width: 560px;
    margin: 0 auto;
  }

  .step-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
  }

  .back-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: var(--sdk-text-secondary, #6B7280);
    border-radius: 8px;
    transition: all 0.2s;
  }

  .back-button:hover {
    background: var(--sdk-bg-secondary, #F3F4F6);
    color: var(--sdk-text, #1F2937);
  }

  .step-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: var(--sdk-primary, #0D9488);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .step-title {
    font-size: 24px;
    font-weight: 600;
    color: var(--sdk-text, #1F2937);
    margin: 0 0 8px;
  }

  .step-description {
    color: var(--sdk-text-secondary, #6B7280);
    font-size: 15px;
    margin: 0 0 24px;
    line-height: 1.5;
  }

  /* Ownership Bar */
  .ownership-bar-container {
    background: var(--sdk-bg-secondary, #F9FAFB);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 20px;
  }

  .ownership-bar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  .ownership-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--sdk-text-secondary, #6B7280);
  }

  .ownership-value {
    font-size: 14px;
    font-weight: 600;
  }

  .ownership-bar-track {
    height: 8px;
    background: var(--sdk-border, #E5E7EB);
    border-radius: 4px;
    overflow: hidden;
  }

  .ownership-bar-fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.3s ease, background-color 0.3s ease;
  }

  /* UBO Cards */
  .ubo-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;
  }

  .ubo-card {
    background: white;
    border: 2px solid var(--sdk-border, #E5E7EB);
    border-radius: 16px;
    overflow: hidden;
    transition: border-color 0.2s;
  }

  .ubo-card.expanded {
    border-color: var(--sdk-primary, #0D9488);
  }

  .ubo-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    cursor: pointer;
    background: var(--sdk-bg-secondary, #F9FAFB);
  }

  .ubo-avatar {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%);
    color: #059669;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .ubo-header-content {
    flex: 1;
    min-width: 0;
  }

  .ubo-title {
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: var(--sdk-text, #1F2937);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ubo-ownership {
    font-size: 13px;
    color: var(--sdk-text-secondary, #6B7280);
  }

  .remove-ubo-btn {
    padding: 8px;
    background: none;
    border: none;
    color: var(--sdk-text-secondary, #9CA3AF);
    cursor: pointer;
    border-radius: 8px;
    transition: all 0.2s;
  }

  .remove-ubo-btn:hover {
    background: #FEE2E2;
    color: #EF4444;
  }

  .ubo-card-body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  /* Form Styles */
  .form-group {
    display: flex;
    flex-direction: column;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  @media (max-width: 480px) {
    .form-row {
      grid-template-columns: 1fr;
    }
  }

  .form-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    color: var(--sdk-text, #374151);
    margin-bottom: 6px;
  }

  .form-label svg {
    color: var(--sdk-text-secondary, #6B7280);
  }

  .form-label .required {
    color: #EF4444;
  }

  .form-label .optional {
    font-weight: 400;
    color: var(--sdk-text-secondary, #9CA3AF);
    margin-left: 4px;
  }

  .form-input,
  .form-select {
    width: 100%;
    padding: 12px 14px;
    font-size: 14px;
    border: 2px solid var(--sdk-border, #E5E7EB);
    border-radius: 10px;
    background: white;
    color: var(--sdk-text, #1F2937);
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    box-sizing: border-box;
  }

  .form-input:focus,
  .form-select:focus {
    border-color: var(--sdk-primary, #0D9488);
    box-shadow: 0 0 0 3px rgba(var(--sdk-primary-rgb, 13, 148, 136), 0.1);
  }

  .form-input.error,
  .form-select.error {
    border-color: #EF4444;
  }

  .form-input::placeholder {
    color: var(--sdk-text-secondary, #9CA3AF);
  }

  .form-select {
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 40px;
  }

  .error-message {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #EF4444;
    margin-top: 6px;
  }

  /* Checkbox Styles */
  .checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 8px;
  }

  .checkbox-label {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
    font-size: 14px;
    color: var(--sdk-text-secondary, #6B7280);
  }

  .checkbox-label input {
    display: none;
  }

  .checkbox-custom {
    width: 20px;
    height: 20px;
    border: 2px solid var(--sdk-border, #E5E7EB);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s;
  }

  .checkbox-label input:checked + .checkbox-custom {
    background: var(--sdk-primary, #0D9488);
    border-color: var(--sdk-primary, #0D9488);
    color: white;
  }

  /* Add UBO Button */
  .add-ubo-button {
    width: 100%;
    padding: 14px;
    background: transparent;
    color: var(--sdk-primary, #0D9488);
    border: 2px dashed var(--sdk-border, #E5E7EB);
    border-radius: 12px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s;
    margin-bottom: 20px;
  }

  .add-ubo-button:hover {
    background: rgba(var(--sdk-primary-rgb, 13, 148, 136), 0.05);
    border-color: var(--sdk-primary, #0D9488);
  }

  /* Errors */
  .total-error {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #FEF2F2;
    border: 1px solid #FECACA;
    border-radius: 10px;
    color: #DC2626;
    font-size: 14px;
    margin-bottom: 20px;
  }

  .submit-error {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #FEF2F2;
    border: 1px solid #FECACA;
    border-radius: 10px;
    color: #DC2626;
    font-size: 14px;
    margin-top: 16px;
  }

  /* Certification */
  .certification-section {
    margin-bottom: 8px;
  }

  .certification-checkbox {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    background: linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%);
    border: 2px solid transparent;
    border-radius: 12px;
    cursor: pointer;
    transition: border-color 0.2s;
  }

  .certification-checkbox.error {
    border-color: #FECACA;
    background: #FEF2F2;
  }

  .certification-checkbox:has(input:checked) {
    border-color: var(--sdk-primary, #0D9488);
  }

  .certification-checkbox input {
    display: none;
  }

  .certification-checkbox .checkbox-custom {
    width: 24px;
    height: 24px;
    border-width: 2px;
    flex-shrink: 0;
  }

  .certification-text {
    font-size: 14px;
    color: var(--sdk-text-secondary, #6B7280);
    line-height: 1.5;
  }

  .certification-text strong {
    color: var(--sdk-text, #1F2937);
  }

  .certification-error {
    margin-top: 8px;
  }

  /* Actions */
  .step-actions {
    margin-top: 20px;
  }

  .primary-button {
    width: 100%;
    padding: 14px 24px;
    background: var(--sdk-primary, #0D9488);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s;
  }

  .primary-button:hover:not(:disabled) {
    background: var(--sdk-primary-dark, #0F766E);
    transform: translateY(-1px);
  }

  .primary-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
